import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MesPropositionsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchProposals();
    } catch (error) {
      toast.error('Vous devez être connecté');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals/sent`, { withCredentials: true });
      setProposals(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => {}}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-7xl mx-auto px-2 md:px-4 pb-12">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-blue-600 mb-6">📤 Mes Propositions</h2>

          {proposals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Aucune proposition envoyée</p>
              <Button onClick={() => navigate('/')} className="rounded-full">
                🔍 Chercher des acquéreurs
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {proposals.map((prop) => (
                <div key={prop.proposal_id} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">
                        {prop.property_type} - {prop.property_address}
                      </h3>
                      <p className="text-xl font-bold text-blue-600 mb-2">
                        💰 {prop.price.toLocaleString('fr-FR')} €
                      </p>
                      <p className="text-sm text-gray-600">📐 {prop.surface} m²</p>
                      <div className="mt-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          prop.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                          prop.status === 'viewed' ? 'bg-blue-100 text-blue-800' :
                          prop.status === 'interested' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {prop.status === 'sent' ? '📨 Envoyée' :
                           prop.status === 'viewed' ? '👁️ Vue' :
                           prop.status === 'interested' ? '✅ Intéressé' : '❌ Déclinée'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Envoyée le {new Date(prop.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    <Button
                      onClick={() => navigate(`/messagerie?proposal=${prop.proposal_id}`)}
                      className="rounded-full bg-blue-600"
                      size="sm"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Messagerie
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MesPropositionsPage;
